import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Receipt, Search, Upload, Eye, FileText, Calendar, Euro,
  MoreHorizontal, CheckCircle, Loader2, Download, AlertCircle,
  Building2, CreditCard, Clock
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

// =============================================
// REÇUS V1 - Version Tableau Compact
// =============================================

const ReceiptsPageV1 = () => {
  const { user, token } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    totalAmount: 0
  });

  const statusConfig = {
    pending_invoice: { label: 'À facturer', color: 'bg-amber-100 text-amber-700', icon: Clock },
    invoice_uploaded: { label: 'Facturée', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700', icon: AlertCircle }
  };

  const paymentMethods = {
    stripe: 'Carte (Stripe)',
    bank_transfer: 'Virement',
    check: 'Chèque',
    cash: 'Espèces'
  };

  useEffect(() => {
    fetchReceipts();
  }, []);

  const fetchReceipts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/receipts/pending');
      const data = response.data || [];
      setReceipts(data);
      
      setStats({
        total: data.length,
        pending: data.filter(r => r.status === 'pending_invoice').length,
        completed: data.filter(r => r.status === 'invoice_uploaded').length,
        totalAmount: data.reduce((sum, r) => sum + (r.amount || 0), 0)
      });
    } catch (error) {
      console.error('Error fetching receipts:', error);
      // Mock data for demo
      const mockData = [
        {
          id: 'rcpt-001',
          client_name: 'BESSON SOLUTIONS',
          client_email: 'contact@besson-solutions.fr',
          payment_date: '2024-12-20',
          amount: 1500,
          method: 'stripe',
          status: 'pending_invoice',
          description: 'Pack 20h'
        }
      ];
      setReceipts(mockData);
      setStats({ total: 1, pending: 1, completed: 0, totalAmount: 1500 });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadInvoice = async () => {
    if (!uploadFile || !selectedReceipt) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      await api.post(`/receipts/${selectedReceipt.id}/upload-invoice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Facture déposée !');
      setShowUploadModal(false);
      setSelectedReceipt(null);
      setUploadFile(null);
      fetchReceipts();
    } catch (error) {
      toast.error('Erreur lors du dépôt');
    } finally {
      setUploading(false);
    }
  };

  const filteredReceipts = receipts.filter(r => {
    const search = searchQuery.toLowerCase();
    const matchesSearch = 
      r.client_name?.toLowerCase().includes(search) ||
      r.description?.toLowerCase().includes(search);
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a365d]">Reçus à facturer</h1>
            <p className="text-[#64748b] text-sm mt-1">
              {stats.pending} en attente · {stats.totalAmount.toLocaleString('fr-FR')}€ total
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="card">
            <CardContent className="p-4">
              <p className="text-xs text-[#64748b]">Total</p>
              <p className="text-2xl font-bold text-[#1a365d]">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="card">
            <CardContent className="p-4">
              <p className="text-xs text-[#64748b]">À facturer</p>
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card className="card">
            <CardContent className="p-4">
              <p className="text-xs text-[#64748b]">Facturées</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
            </CardContent>
          </Card>
          <Card className="card">
            <CardContent className="p-4">
              <p className="text-xs text-[#64748b]">Montant total</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalAmount.toLocaleString('fr-FR')}€</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'pending_invoice', 'invoice_uploaded'].map(status => (
              <Button
                key={status}
                variant={filterStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(status)}
                className={cn(filterStatus === status && 'bg-[#1a365d]')}
              >
                {status === 'all' ? 'Tous' : statusConfig[status]?.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        <Card className="card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Méthode</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#64748b]" />
                  </TableCell>
                </TableRow>
              ) : filteredReceipts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#64748b]">
                    Aucun reçu trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredReceipts.map(receipt => {
                  const status = statusConfig[receipt.status] || statusConfig.pending_invoice;
                  const StatusIcon = status.icon;
                  return (
                    <TableRow key={receipt.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#64748b]" />
                          <div>
                            <p className="font-medium text-[#1a365d]">{receipt.client_name}</p>
                            <p className="text-xs text-[#64748b]">{receipt.client_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{receipt.description || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-[#64748b]">
                          <Calendar className="w-3 h-3" />
                          {new Date(receipt.payment_date).toLocaleDateString('fr-FR')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <CreditCard className="w-3 h-3 text-[#64748b]" />
                          {paymentMethods[receipt.method] || receipt.method}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        {receipt.amount?.toLocaleString('fr-FR')}€
                      </TableCell>
                      <TableCell>
                        <Badge className={cn("text-xs", status.color)}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedReceipt(receipt);
                              setShowUploadModal(true);
                            }}>
                              <Upload className="w-4 h-4 mr-2" /> Déposer facture
                            </DropdownMenuItem>
                            {receipt.invoice_url && (
                              <DropdownMenuItem onClick={() => window.open(receipt.invoice_url, '_blank')}>
                                <Download className="w-4 h-4 mr-2" /> Télécharger facture
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => {
                              setSelectedReceipt(receipt);
                              setShowDetailModal(true);
                            }}>
                              <Eye className="w-4 h-4 mr-2" /> Voir détails
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Upload Invoice Modal */}
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                Déposer la facture
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedReceipt && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-[#1a365d]">{selectedReceipt.client_name}</p>
                  <p className="text-sm text-[#64748b]">{selectedReceipt.description}</p>
                  <p className="text-lg font-bold text-[#1a365d] mt-1">
                    {selectedReceipt.amount?.toLocaleString('fr-FR')}€
                  </p>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Facture Axonaut (PDF)</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-amber-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setUploadFile(e.target.files?.[0])}
                    className="hidden"
                    id="invoice-upload"
                  />
                  <label htmlFor="invoice-upload" className="cursor-pointer">
                    {uploadFile ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileText className="w-5 h-5 text-emerald-500" />
                        <span className="font-medium text-[#1a365d]">{uploadFile.name}</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto mb-2 text-[#64748b]" />
                        <p className="text-sm text-[#64748b]">
                          Cliquez pour sélectionner un PDF
                        </p>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleUploadInvoice} 
                disabled={!uploadFile || uploading}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Déposer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-500" />
                Détails du reçu
              </DialogTitle>
            </DialogHeader>
            {selectedReceipt && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Client</p>
                    <p className="font-medium text-[#1a365d]">{selectedReceipt.client_name}</p>
                    <p className="text-sm text-slate-500">{selectedReceipt.client_email}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Montant</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {selectedReceipt.amount?.toLocaleString('fr-FR')}€
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500">Description</span>
                    <span className="font-medium">{selectedReceipt.description || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500">Date de paiement</span>
                    <span className="font-medium">
                      {selectedReceipt.payment_date ? new Date(selectedReceipt.payment_date).toLocaleDateString('fr-FR') : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500">Méthode</span>
                    <span className="font-medium">{paymentMethods[selectedReceipt.method] || selectedReceipt.method}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500">Référence</span>
                    <span className="font-mono text-sm">{selectedReceipt.reference || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-slate-500">Statut</span>
                    <Badge className={cn("text-xs", (statusConfig[selectedReceipt.status] || statusConfig.pending_invoice).color)}>
                      {(statusConfig[selectedReceipt.status] || statusConfig.pending_invoice).label}
                    </Badge>
                  </div>
                  {selectedReceipt.project_name && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-500">Projet</span>
                      <span className="font-medium">{selectedReceipt.project_name}</span>
                    </div>
                  )}
                </div>

                {selectedReceipt.invoice_url && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(selectedReceipt.invoice_url, '_blank')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger la facture
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ReceiptsPageV1;
